/**
 * <s-scroll-bar>: thin progress bar under a carousel (instead of arrows).
 * Finds the [data-carousel-track] in the same parent, sizes the thumb to the visible part
 * and moves it while scrolling. Click or drag the bar to scroll the carousel.
 * Markup: <s-scroll-bar><div data-scroll-rail><span data-scroll-thumb></span></div></s-scroll-bar>
 */
class ScrollBar extends HTMLElement {
  connectedCallback() {
    this.track = this.parentElement?.querySelector("[data-carousel-track]");
    this.rail = this.querySelector("[data-scroll-rail]");
    this.thumb = this.querySelector("[data-scroll-thumb]");
    if (!this.track || !this.rail || !this.thumb) return;

    this.update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = this.track;
      const max = scrollWidth - clientWidth;
      this.hidden = max <= 1;
      const size = Math.max(clientWidth / scrollWidth, 0.08);
      const rtl = getComputedStyle(this.track).direction === "rtl";
      const progress = max > 0 ? Math.abs(scrollLeft) / max : 0;
      this.thumb.style.width = `${size * 100}%`;
      const offset = progress * (1 - size) * 100;
      this.thumb.style.insetInlineStart = `${offset}%`;
      this.rtl = rtl;
    };

    this.scrollToPointer = (event) => {
      const rect = this.rail.getBoundingClientRect();
      let ratio = (event.clientX - rect.left) / rect.width;
      ratio = Math.min(Math.max(ratio, 0), 1);
      if (this.rtl) ratio = 1 - ratio;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = ratio * max * (this.rtl ? -1 : 1);
      this.track.scrollTo({ left, behavior: event.type === "pointerdown" ? "smooth" : "auto" });
    };

    this.onDown = (event) => {
      this.dragging = true;
      this.rail.setPointerCapture?.(event.pointerId);
      this.track.style.scrollSnapType = "none";
      this.scrollToPointer(event);
    };
    this.onMove = (event) => {
      if (this.dragging) this.scrollToPointer(event);
    };
    this.onUp = () => {
      this.dragging = false;
      this.track.style.scrollSnapType = "";
    };

    this.track.addEventListener("scroll", this.update, { passive: true });
    window.addEventListener("resize", this.update);
    this.rail.addEventListener("pointerdown", this.onDown);
    this.rail.addEventListener("pointermove", this.onMove);
    this.rail.addEventListener("pointerup", this.onUp);
    this.rail.addEventListener("pointercancel", this.onUp);
    this.update();
    setTimeout(this.update, 500);
  }

  disconnectedCallback() {
    this.track?.removeEventListener("scroll", this.update);
    window.removeEventListener("resize", this.update);
  }
}

if (!customElements.get("s-scroll-bar")) customElements.define("s-scroll-bar", ScrollBar);
